import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  Wrench,
  CheckCircle,
  Briefcase,
  Settings,
  Award,
  Monitor
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import procurementServiceMasterService from '../../services/procurementServiceMasterService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const ProcurementServiceMaster = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalServices: 0,
    activeServices: 0,
    inactiveServices: 0,
    consultingType: 0,
    maintenanceType: 0,
    professionalType: 0,
    itServicesType: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [servicesResponse, statsResponse] = await Promise.all([
        procurementServiceMasterService.getAllProcurementServices(),
        procurementServiceMasterService.getProcurementServiceStats()
      ])
      
      if (servicesResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (servicesResponse.data || []).map(service => ({
          ...service,
          id: service._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (servicesResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No procurement service records found. Create your first service!')
        }
      } else {
        toast.error(servicesResponse.message || 'Failed to load procurement services')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading procurement service master records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view procurement services')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to load procurement service master records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const getServiceTypeBadgeClass = (type) => {
    const typeMap = {
      'Consulting': 'active',
      'Maintenance': 'warning',
      'Professional': 'pending',
      'IT Services': 'active',
      'Support': 'pending',
      'Training': 'inactive',
      'Other': 'inactive'
    }
    return typeMap[type] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'serviceCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'serviceName',
      header: 'Service Name',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'serviceType',
      header: 'Type',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getServiceTypeBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'category',
      header: 'Category',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>
          {getValue() || '-'}
        </div>
      ),
    },
    {
      accessorKey: 'pricing.costPrice',
      header: 'Cost Price',
      cell: ({ row }) => {
        const price = row.original.pricing?.costPrice || row.original.pricing?.basePrice || 0
        const currency = row.original.pricing?.currency || 'INR'
        const unit = row.original.unitOfMeasurement || 'Unit'
        return (
          <div style={{ textAlign: 'right', fontWeight: '600' }}>
            {new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(price)} / {unit}
          </div>
        )
      },
    },
    {
      accessorKey: 'unitOfMeasurement',
      header: 'Unit',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          {getValue() || '-'}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getValue()?.toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/procurement/procurement-service-master/form')
  }

  const handleEdit = (serviceData) => {
    navigate('/procurement/procurement-service-master/edit', { state: { serviceData } })
  }

  const handleView = (serviceData) => {
    navigate('/procurement/procurement-service-master/view', { state: { serviceData } })
  }

  const handleDelete = (serviceData) => {
    setDeleteItem(serviceData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting procurement service...')
      const response = await procurementServiceMasterService.deleteProcurementService(deleteItem._id)
      if (response.success) {
        toast.success('Procurement service deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete procurement service')
      }
    } catch (error) {
      console.error('Error deleting procurement service:', error)
      if (error.response?.status === 404) {
        toast.error('Procurement service not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this procurement service')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to delete procurement service. Please try again.')
        }
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  const breadcrumbItems = [
    { label: 'Procurement', path: '/procurement' },
    { label: 'Masters', path: '/procurement?tab=masters' },
    { label: 'Services', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>Services</h1>
          <p>Service catalog for procurement</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Service
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <Wrench size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalServices || 0}</div>
          <div className="metric-title">Total Services</div>
          <div className="metric-subtitle">All service records</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeServices || 0}</div>
          <div className="metric-title">Active Services</div>
          <div className="metric-subtitle">Available to procure</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Briefcase size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Consulting</span>
            </div>
          </div>
          <div className="metric-value">{stats.consultingType || 0}</div>
          <div className="metric-title">Consulting</div>
          <div className="metric-subtitle">Consulting type</div>
        </div>

        <div className="metric-card conversion">
          <div className="metric-header">
            <div className="metric-icon">
              <Settings size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Maintenance</span>
            </div>
          </div>
          <div className="metric-value">{stats.maintenanceType || 0}</div>
          <div className="metric-title">Maintenance</div>
          <div className="metric-subtitle">Maintenance type</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Award size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Professional</span>
            </div>
          </div>
          <div className="metric-value">{stats.professionalType || 0}</div>
          <div className="metric-title">Professional</div>
          <div className="metric-subtitle">Professional services</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Monitor size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>IT</span>
            </div>
          </div>
          <div className="metric-value">{stats.itServicesType || 0}</div>
          <div className="metric-title">IT Services</div>
          <div className="metric-subtitle">Technology services</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          enableSelection={false}
          noCard={true}
          className="procurement-service-master-table"
          pageSize={10}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Procurement Service"
        message={`Are you sure you want to delete service "${deleteItem?.serviceName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default ProcurementServiceMaster

